import React from 'react';
import {
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type {
  ImportedInvoiceParserMetadataDto,
  InvoiceImportFa3Disposition,
  InvoiceImportFa3Family,
  InvoiceImportFa3LineRole,
  InvoiceImportFa3LineSource,
  InvoiceImportFa3SemanticPreviewDto,
  InvoiceImportFa3SupportStatus,
} from '../Types/invoiceImport.types';

const FAMILY_LABELS: Record<InvoiceImportFa3Family, string> = {
  prepayment: 'Zaliczkowa',
  finalSettlement: 'Rozliczeniowa',
  standardCorrection: 'Korekta standardowa',
  prepaymentCorrection: 'Korekta zaliczki',
  settlementCorrection: 'Korekta rozliczenia',
  formalCorrection: 'Korekta formalna',
  collectiveCorrection: 'Korekta zbiorcza',
  mixedTaxation: 'Mieszana stawka',
  margin: 'Procedura marży',
  simplified: 'Uproszczona',
  intraCommunitySupply: 'WDT',
  export: 'Eksport',
  standardVat: 'VAT standardowy',
};

const DISPOSITION_LABELS: Record<InvoiceImportFa3Disposition, string> = {
  autoImport: 'Auto-import',
  reviewRequired: 'Do weryfikacji',
};

const SUPPORT_STATUS_LABELS: Record<InvoiceImportFa3SupportStatus, string> = {
  supported: 'Wspierane',
  unsupported: 'Niewspierane',
};

const LINE_SOURCE_LABELS: Record<InvoiceImportFa3LineSource, string> = {
  faWiersz: 'FAWiersz',
  zamowienieWiersz: 'Wiersz zamówienia',
  none: 'Brak',
};

const LINE_ROLE_LABELS: Record<InvoiceImportFa3LineRole, string> = {
  before: 'Stan przed',
  after: 'Stan po',
  order: 'Powiązanie zamówienia',
  current: 'Pozycja bieżąca',
  delta: 'Różnica',
  referenceOnly: 'Referencja',
};

const RELATION_TYPE_LABELS: Record<InvoiceImportFa3SemanticPreviewDto['links'][number]['relationType'], string> = {
  correctedDocument: 'Dokument korygowany',
  prepaymentDocument: 'Dokument zaliczkowy',
  settlementDocument: 'Dokument rozliczany',
};

const CORRECTION_VARIANT_LABELS: Record<NonNullable<InvoiceImportFa3SemanticPreviewDto['correctionVariant']>, string> = {
  beforeAfter: 'Przed / po',
  difference: 'Różnicowa',
  formalOnly: 'Formalna',
  collectivePeriod: 'Zbiorcza okresowa',
};

interface ImportFa3SemanticPreviewProps {
  preview?: InvoiceImportFa3SemanticPreviewDto | null;
  parserMetadata?: ImportedInvoiceParserMetadataDto | null;
  title?: string;
}

const renderField = (label: string, value: React.ReactNode) => (
  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Grid>
);

export const ImportFa3SemanticPreview: React.FC<ImportFa3SemanticPreviewProps> = ({
  preview,
  parserMetadata,
  title = 'Semantyka FA(3)',
}) => {
  if (!preview) {
    return null;
  }

  const overlayFlags = parserMetadata?.overlayFlags ?? [];
  const needsReview =
    preview?.disposition === 'reviewRequired' ||
    overlayFlags.includes('reviewRequired') ||
    overlayFlags.includes('manualReview');
  const unresolvedLinks = preview?.links.some((link) => !link.isResolved) ?? false;

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {needsReview ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Semantyka FA(3) wskazuje ścieżkę <strong>Do weryfikacji</strong>.
          </Alert>
        ) : null}

        {preview.supportStatus === 'unsupported' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {preview.unsupportedReason ? (
              <>
                Powód braku wsparcia: <strong>{preview.unsupportedReason}</strong>
              </>
            ) : (
              <>Backend oznaczył tę semantykę FA(3) jako niewspieraną.</>
            )}
          </Alert>
        ) : null}

        {unresolvedLinks ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Nierozwiązane relacje dokumentów wymagają decyzji przed zatwierdzeniem.
          </Alert>
        ) : null}

        <Grid container spacing={3}>
          {renderField('Rodzina FA(3)', FAMILY_LABELS[preview.family])}
          {renderField('Dyspozycja', DISPOSITION_LABELS[preview.disposition])}
          {renderField('Status wsparcia', SUPPORT_STATUS_LABELS[preview.supportStatus])}
          {renderField('Źródło linii', LINE_SOURCE_LABELS[preview.lineSource])}
          {renderField('Rodzaj źródła', parserMetadata?.sourceDocumentKind || '—')}
          {renderField('Źródłowy numer KSeF', parserMetadata?.sourceKsefNumber || '—')}
          {renderField(
            'Wariant korekty',
            preview.correctionVariant ? CORRECTION_VARIANT_LABELS[preview.correctionVariant] : '—'
          )}
          {renderField(
            'Powód korekty',
            preview.correctionReason || parserMetadata?.correctionReason || '—'
          )}
        </Grid>

        {overlayFlags.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Flagi parsera
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {overlayFlags.map((flag) => (
                <Chip key={flag} size="small" label={flag} variant="outlined" />
              ))}
            </Stack>
          </>
        ) : null}

        {preview.links.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Relacje dokumentów
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Typ relacji</TableCell>
                    <TableCell>Referencja</TableCell>
                    <TableCell>Numer KSeF</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.links.map((link, index) => (
                    <TableRow key={`${link.relationType}-${index}`}>
                      <TableCell>{RELATION_TYPE_LABELS[link.relationType]}</TableCell>
                      <TableCell>
                        {link.relatedDocumentReference || link.externalInvoiceNumber || '—'}
                      </TableCell>
                      <TableCell>{link.externalKsefNumber || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={link.isResolved ? 'success' : 'warning'}
                          variant="outlined"
                          label={link.isResolved ? 'Rozwiązana' : 'Do rozwiązania'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}

        {preview.lines.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Role linii
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Rola</TableCell>
                    <TableCell>Sekcja źródła</TableCell>
                    <TableCell>Nr linii</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.lines.map((line, index) => (
                    <TableRow key={`${line.lineRole}-${index}`}>
                      <TableCell>{LINE_ROLE_LABELS[line.lineRole]}</TableCell>
                      <TableCell>{line.sourceSection}</TableCell>
                      <TableCell>{line.sourceLineNumber ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default ImportFa3SemanticPreview;
